import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaCrosshairs,
  FaMapMarkerAlt,
  FaCheckCircle,
  FaSearch,
  FaPlus,
  FaTrash,
  FaTimes,
} from "react-icons/fa";
import ConfirmationModal from "./ConfirmationModal";

const LocationModal2 = ({
  show,
  onClose,
  onAddressAdded,
  onAddressSelected,
  selectedLocationId,
}) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [primaryAddressId, setPrimaryAddressId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const searchInputRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const fetchAbortControllerRef = useRef(null);
  const hasFetchedAddressesRef = useRef(false);
  const isModalMountedRef = useRef(false);
  const modalContentRef = useRef(null);
  const savedAddressesRef = useRef(null);
  const modalRef = useRef(null);

  const isTablet = window.innerWidth <= 1024;

  const API_KEY = process.env.REACT_APP_MAP_KEY;

  // Update window dimensions
  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle body scroll lock
  useEffect(() => {
    if (show) {
      // Store current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
      document.body.style.overflow = "hidden";

      return () => {
        // Restore scroll position
        const scrollY = document.body.style.top;
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
        document.body.style.overflow = "";
        window.scrollTo(0, parseInt(scrollY || "0") * -1);
      };
    }
  }, [show]);

  // Initialize Google Maps services
  const initializeServices = useCallback(() => {
    console.log("Initializing Google Maps services...");

    if (!window.google?.maps?.places) {
      console.error("Google Maps Places API not available");
      return;
    }

    try {
      if (!autocompleteServiceRef.current) {
        autocompleteServiceRef.current =
          new window.google.maps.places.AutocompleteService();
        console.log("AutocompleteService initialized successfully");
      }

      if (!placesServiceRef.current) {
        placesServiceRef.current = new window.google.maps.places.PlacesService(
          document.createElement("div"),
        );
        console.log("PlacesService initialized successfully");
      }
    } catch (error) {
      console.error("Error initializing Google Maps services:", error);
    }
  }, []);

  // Optimized Google Maps script loading with caching check
  useEffect(() => {
    if (!show) return;

    isModalMountedRef.current = true;

    console.log("Modal opened, checking Google Maps API...");

    // Check if already loaded
    if (window.google?.maps?.places) {
      console.log("Google Maps API already loaded");
      setScriptLoaded(true);
      initializeServices();
      return;
    }

    // Check if script element exists
    const existingScript = document.querySelector(
      `script[src*="maps.googleapis.com"]`,
    );

    if (existingScript) {
      console.log("Google Maps script found, waiting for load...");
      // Script is loading, wait for it
      const checkInterval = setInterval(() => {
        if (window.google?.maps?.places) {
          console.log("Google Maps API loaded via existing script");
          setScriptLoaded(true);
          initializeServices();
          clearInterval(checkInterval);
        }
      }, 50);

      // Timeout after 10 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        if (!window.google?.maps?.places) {
          console.error("Google Maps API failed to load within timeout");
        }
      }, 10000);

      return () => clearInterval(checkInterval);
    }

    // Load new script
    console.log("Loading Google Maps script...");
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&libraries=places&loading=async`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      console.log("Google Maps script loaded successfully");
      if (isModalMountedRef.current) {
        setScriptLoaded(true);
        initializeServices();
      }
    };

    script.onerror = (error) => {
      console.error("Failed to load Google Maps script:", error);
    };

    document.head.appendChild(script);

    return () => {
      isModalMountedRef.current = false;
    };
  }, [show, API_KEY, initializeServices]);

  // Focus search input
  useEffect(() => {
    if (show && searchInputRef.current) {
      const timer = setTimeout(() => searchInputRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [show]);

  const [selectUser, setSelectUser] = useState();

  // Fetch saved addresses
  const fetchSavedAddresses = useCallback(
    async (forceRefresh = false) => {
      try {
        setLoading(true);

        const user = JSON.parse(localStorage.getItem("user"));
        setSelectUser(user);
        const customerId = user?._id;

        if (!customerId) {
          setLoading(false);
          return;
        }

        // Only use cache if not forced refresh
        if (!forceRefresh) {
          const cachedAddresses = localStorage.getItem(
            `addresses_${customerId}`,
          );
          const cacheTimestamp = localStorage.getItem(
            `addresses_timestamp_${customerId}`,
          );
          const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

          // Use cache if valid and recent
          if (cachedAddresses && cacheTimestamp) {
            const age = Date.now() - parseInt(cacheTimestamp);
            if (age < CACHE_DURATION) {
              const cached = JSON.parse(cachedAddresses);
              setSavedAddresses(cached.addresses || []);
              setPrimaryAddressId(cached.primaryAddress || null);
              setLoading(false);

              // Still fetch fresh data in background
              fetchAddressesInBackground(customerId);
              return;
            }
          }
        }

        // Create AbortController for cancellation
        fetchAbortControllerRef.current = new AbortController();

        const response = await fetch(
          `https://dailydish.in/api/User/customers/${customerId}/addresses`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
            signal: fetchAbortControllerRef.current.signal,
          },
        );

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            const addresses = result.addresses || [];
            const primaryId = result.primaryAddress || null;

            setSavedAddresses(addresses);
            setPrimaryAddressId(primaryId);

            // Cache the results
            localStorage.setItem(
              `addresses_${customerId}`,
              JSON.stringify({ addresses, primaryAddress: primaryId }),
            );
            localStorage.setItem(
              `addresses_timestamp_${customerId}`,
              Date.now().toString(),
            );

            // Notify parent component if address was just added
            if (forceRefresh && onAddressAdded) {
              onAddressAdded();
            }
          }
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching addresses:", error);
        }
      } finally {
        setLoading(false);
        hasFetchedAddressesRef.current = true;
      }
    },
    [onAddressAdded],
  );

  // Background fetch to update cache
  const fetchAddressesInBackground = async (customerId) => {
    try {
      const response = await fetch(
        `https://dailydish.in/api/User/customers/${customerId}/addresses`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          const addresses = result.addresses || [];
          const primaryId = result.primaryAddress || null;

          // Update state if modal is still open
          if (isModalMountedRef.current) {
            setSavedAddresses(addresses);
            setPrimaryAddressId(primaryId);
          }

          // Update cache
          localStorage.setItem(
            `addresses_${customerId}`,
            JSON.stringify({ addresses, primaryAddress: primaryId }),
          );
          localStorage.setItem(
            `addresses_timestamp_${customerId}`,
            Date.now().toString(),
          );
        }
      }
    } catch (error) {
      console.error("Background fetch error:", error);
    }
  };

  // Listen for address added events
  useEffect(() => {
    const handleAddressAdded = () => {
      // Force refresh addresses when an address is added
      fetchSavedAddresses(true);

      // Also refresh the primary address display
      const primary = localStorage.getItem("primaryAddress");
      if (primary) {
        try {
          const primaryData = JSON.parse(primary);
          setPrimaryAddressId(primaryData._id);
        } catch (e) {
          console.error("Error parsing primary address:", e);
        }
      }
    };

    // Custom event listener
    window.addEventListener("addressAdded", handleAddressAdded);

    return () => {
      window.removeEventListener("addressAdded", handleAddressAdded);
    };
  }, [fetchSavedAddresses]);

  // Fetch addresses when modal opens
  useEffect(() => {
    if (show) {
      fetchSavedAddresses();
    }

    return () => {
      // Cleanup: abort ongoing fetch
      if (fetchAbortControllerRef.current) {
        fetchAbortControllerRef.current.abort();
      }
    };
  }, [show, fetchSavedAddresses]);

  // Debounced search with cleanup
  const handleSearchChange = useCallback(
    (e) => {
      const query = e.target.value;
      setSearchQuery(query);

      console.log("query:", query);
      console.log("search suggestions:", searchSuggestions);
      console.log("Google Maps loaded:", !!window.google?.maps?.places);
      console.log(
        "AutocompleteService initialized:",
        !!autocompleteServiceRef.current,
      );

      // Clear previous timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Check if Google Maps is loaded
      if (!window.google?.maps?.places) {
        console.error("Google Maps Places API not loaded");
        return;
      }

      // Initialize service if not already done
      if (!autocompleteServiceRef.current) {
        console.log("Initializing AutocompleteService...");
        try {
          autocompleteServiceRef.current =
            new window.google.maps.places.AutocompleteService();
        } catch (error) {
          console.error("Failed to initialize AutocompleteService:", error);
          return;
        }
      }

      if (query.length > 2) {
        debounceTimerRef.current = setTimeout(() => {
          console.log("Making API call for query:", query);

          autocompleteServiceRef.current.getPlacePredictions(
            {
              input: query,
              types: ["geocode", "establishment"],
              componentRestrictions: { country: "in" },
            },
            (predictions, status) => {
              console.log("API Response - Status:", status);
              console.log("API Response - Predictions:", predictions);

              if (
                status === window.google.maps.places.PlacesServiceStatus.OK &&
                predictions
              ) {
                console.log("Setting suggestions:", predictions);
                setSearchSuggestions(predictions);
              } else {
                console.log("No predictions or error status:", status);
                setSearchSuggestions([]);

                // Log specific error statuses
                if (
                  status ===
                  window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS
                ) {
                  console.log("No results found for query");
                } else if (
                  status ===
                  window.google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT
                ) {
                  console.error("Google Places API quota exceeded");
                } else if (
                  status ===
                  window.google.maps.places.PlacesServiceStatus.REQUEST_DENIED
                ) {
                  console.error(
                    "Google Places API request denied - check API key",
                  );
                } else if (
                  status ===
                  window.google.maps.places.PlacesServiceStatus.INVALID_REQUEST
                ) {
                  console.error("Invalid request to Google Places API");
                }
              }
            },
          );
        }, 300);
      } else {
        setSearchSuggestions([]);
      }
    },
    [searchSuggestions],
  );

  // Handle Add Location button click
  const handleAddLocation = useCallback(() => {
    onClose();
    navigate("/location", {
      state: {
        showAddressForm: true,
        fromModal: true,
      },
    });
  }, [navigate, onClose]);

  // Handle location selection from search
  const handleLocationSelect = useCallback(
    (place) => {
      if (!placesServiceRef.current) {
        placesServiceRef.current = new window.google.maps.places.PlacesService(
          document.createElement("div"),
        );
      }

      setActionLoading("search");
      setShowSearch(false);

      placesServiceRef.current.getDetails(
        {
          placeId: place.place_id,
          fields: ["geometry", "name", "formatted_address", "vicinity"],
        },
        (placeResult, status) => {
          setActionLoading(null);
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            const locationData = {
              lat: placeResult.geometry.location.lat(),
              lng: placeResult.geometry.location.lng(),
            };

            onClose();
            navigate("/current-location", {
              state: {
                selectedPlace: {
                  location: locationData,
                  address: placeResult.formatted_address,
                  name:
                    placeResult.name ||
                    placeResult.formatted_address.split(",")[0],
                  vicinity: placeResult.vicinity,
                },
                setAsPrimary: false,
                fromSearch: true,
              },
            });
          }
        },
      );
    },
    [navigate, onClose],
  );

  // Handle adding new address
  const handleAddNewAddress = useCallback(() => {
    onClose();
    navigate("/location", {
      state: {
        showAddressForm: true,
        fromModal: true,
      },
    });
  }, [navigate, onClose]);

  // Handle current location
  const handleUseCurrentLocation = useCallback(() => {
    if (actionLoading) return;

    setActionLoading("location");
    onClose();
    navigate("/current-location", {
      state: {
        setAsPrimary: true,
        source: "addressDrawer",
      },
    });
    // Note: The loading state will be handled by the current-location page
  }, [navigate, onClose, actionLoading]);

  // Handle deliver here
  const handleDeliverHere = useCallback(
    async (address) => {
      if (actionLoading) return;

      setActionLoading(address._id);

      try {
        const user = JSON.parse(localStorage.getItem("user"));
        const customerId = user?._id;

        if (!customerId) return;

        // If onAddressSelected callback is provided (e.g., from MyPlan), call it instead of reloading
        if (onAddressSelected) {
          onAddressSelected(address);
          setActionLoading(null);
          return;
        }

        // Update UI immediately
        setPrimaryAddressId(address._id);
        localStorage.setItem("primaryAddress", JSON.stringify(address));
        // Set manual location flag to prevent auto-detection
        localStorage.setItem("locationManuallySelected", "true");
        localStorage.removeItem("cart");
        // Update cache
        const cachedAddresses = localStorage.getItem(`addresses_${customerId}`);
        if (cachedAddresses) {
          const cached = JSON.parse(cachedAddresses);
          cached.primaryAddress = address._id;
          localStorage.setItem(
            `addresses_${customerId}`,
            JSON.stringify(cached),
          );
        }

        // Close modal first
        onClose();

        // API call
        const response = await fetch(
          `https://dailydish.in/api/User/customers/${customerId}/addresses/${address._id}/primary`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
          },
        );

        if (response.ok) {
          // Trigger refresh everywhere
          window.dispatchEvent(new Event("addressUpdated"));

          // Reload after a short delay
          setTimeout(() => {
            window.location.reload();
          }, 100);
        }
      } catch (error) {
        console.error("Error setting primary address:", error);
      } finally {
        setActionLoading(null);
      }
    },
    [onClose, actionLoading, onAddressSelected],
  );

  // Handle delete address confirmation
  const handleDeleteClick = useCallback((address) => {
    setAddressToDelete(address);
    setShowDeleteConfirm(true);
  }, []);

  // Handle delete address
  const handleDeleteAddress = useCallback(async () => {
    if (!addressToDelete) return;

    try {
      setActionLoading(`delete_${addressToDelete._id}`);
      const user = JSON.parse(localStorage.getItem("user"));
      const customerId = user?._id;

      if (!customerId) {
        throw new Error("Customer ID not found. Please login again.");
      }

      const response = await fetch(
        `https://dailydish.in/api/User/customers/${customerId}/addresses/${addressToDelete._id}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to delete address");
      }

      const result = await response.json();

      if (result.success) {
        // Update local state immediately
        const updatedAddresses = savedAddresses.filter(
          (addr) => addr._id !== addressToDelete._id,
        );
        setSavedAddresses(updatedAddresses);

        // If deleted address was primary, clear primary address
        let updatedPrimaryId = primaryAddressId;
        if (primaryAddressId === addressToDelete._id) {
          updatedPrimaryId = null;
          setPrimaryAddressId(null);
          localStorage.removeItem("primaryAddress");
        }

        // Update cache
        localStorage.setItem(
          `addresses_${customerId}`,
          JSON.stringify({
            addresses: updatedAddresses,
            primaryAddress: updatedPrimaryId,
          }),
        );
        localStorage.setItem(
          `addresses_timestamp_${customerId}`,
          Date.now().toString(),
        );

        // Trigger refresh everywhere
        window.dispatchEvent(new Event("addressUpdated"));

        // Close delete modal
        setShowDeleteConfirm(false);
        setAddressToDelete(null);
      } else {
        throw new Error(result.message || "Failed to delete address");
      }
    } catch (error) {
      console.error("Error deleting address:", error);
      alert(error.message || "Failed to delete address");
    } finally {
      setActionLoading(null);
    }
  }, [savedAddresses, primaryAddressId, addressToDelete]);

  // Handle cancel delete
  const handleCancelDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    setAddressToDelete(null);
  }, []);

  // Helper functions
  const getPrimaryName = useCallback((address) => {
    if (!address) return "";

    switch (address.addressType) {
      case "Home":
        return address.homeName || address.houseName || "";
      case "PG":
        return address.apartmentName || address.houseName || "";
      case "School":
        return address.schoolName || address.houseName || "";
      case "Work":
        return address.companyName || address.houseName || "";
      default:
        return address.houseName || "";
    }
  }, []);

  const getAddressIcon = useCallback((type) => {
    switch (type) {
      case "Home":
        return "🏠";
      case "PG":
        return "🏢";
      case "School":
        return "🎓";
      case "Work":
        return "💼";
      default:
        return "📍";
    }
  }, []);

  const isPrimaryAddress = useCallback(
    (addressId) => primaryAddressId === addressId,
    [primaryAddressId],
  );

  const getPrimaryAddress = useCallback(
    () => savedAddresses.find((addr) => addr._id === primaryAddressId),
    [savedAddresses, primaryAddressId],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (fetchAbortControllerRef.current) {
        fetchAbortControllerRef.current.abort();
      }
      isModalMountedRef.current = false;
    };
  }, []);

  if (!show) return null;

  return (
    <div
      ref={modalRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "#ffffff",
        zIndex: 999999,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        width: "100%",
        maxWidth: isTablet ? "100%" : "610px",
        margin: isTablet ? "0" : "0 auto",
      }}
    >
      {/* Header - Fixed */}
      <div
        style={{
          padding: isMobile ? "20px 16px 16px" : "24px 20px 20px",
          borderBottom: "1px solid #f0f0f0",
          flexShrink: 0,
          backgroundColor: "white",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            marginBottom: "12px",
          }}
        >
          <h5
            style={{
              margin: 0,
              color: "#2c2c2c",
              fontFamily: "Inter",
              fontWeight: "600",
              fontSize: isMobile ? "20px" : "24px",
            }}
          >
            Delivery Location
          </h5>

          <button
            type="button"
            onClick={onClose}
            style={{
              fontSize: isMobile ? "24px" : "28px",
              border: "none",
              background: "none",
              cursor: "pointer",
              color: "#555",
              lineHeight: "1",
              flexShrink: 0,
              padding: "4px",
              width: isMobile ? "36px" : "40px",
              height: isMobile ? "36px" : "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              transition: "background-color 0.2s",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "rgba(0,0,0,0.1)";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "transparent";
            }}
          >
            ×
          </button>
        </div>

        <p
          style={{
            margin: 0,
            color: "#666",
            fontSize: isMobile ? "14px" : "16px",
            fontFamily: "Inter",
            lineHeight: "1.4",
          }}
        >
          Select or add a delivery address
        </p>
      </div>

      {/* Search Input */}

      {selectUser ? (
        ""
      ) : (
        <div
          style={{
            padding: "20px",
            position: "relative",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              border: "1px solid #e0e0e0",
              zIndex: 1000,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                padding: "12px 16px",
                gap: "8px",
              }}
            >
              <FaSearch size={18} color="#666" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => setShowSearch(true)}
                placeholder="Search for area, street, landmark..."
                style={{
                  flex: 1,
                  border: "none",
                  outline: "none",
                  fontSize: "16px",
                  padding: "8px 0",
                  backgroundColor: "transparent",
                  fontFamily: "Inter",
                }}
              />
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setSearchSuggestions([]);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "18px",
                    color: "#666",
                    cursor: "pointer",
                    padding: "4px",
                  }}
                >
                  ×
                </button>
              )}
            </div>

            {showSearch && searchSuggestions.length > 0 && (
              <div
                style={{
                  borderTop: "1px solid #e0e0e0",
                  maxHeight: "200px",
                  overflowY: "auto",
                }}
              >
                {searchSuggestions.map((place, index) => (
                  <div
                    key={place.place_id}
                    onClick={() => handleLocationSelect(place)}
                    style={{
                      padding: "12px 16px",
                      borderBottom:
                        index < searchSuggestions.length - 1
                          ? "1px solid #f0f0f0"
                          : "none",
                      cursor: "pointer",
                      transition: "background-color 0.2s",
                      fontSize: "14px",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "#f8f9fa";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "white";
                    }}
                  >
                    <div style={{ fontWeight: "500", color: "#333" }}>
                      {place.structured_formatting.main_text}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        marginTop: "2px",
                      }}
                    >
                      {place.structured_formatting.secondary_text}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content - Scrollable but fits screen */}
      <div
        style={{
          flex: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#f8f9fa",
        }}
      >
        {/* Action Buttons */}
        {selectUser ? (
          <div
            style={{
              padding: isMobile ? "16px" : "20px",
              paddingBottom: isMobile ? "16px" : "20px",
              flexShrink: 0,
              backgroundColor: "white",
            }}
          >
            <button
              onClick={handleAddLocation}
              style={{
                width: "100%",
                padding: isMobile ? "16px" : "18px",
                backgroundColor: "transparent",
                border: "2px dashed #6B8E23",
                borderRadius: "12px",
                color: "#6B8E23",
                fontSize: isMobile ? "15px" : "16px",
                fontWeight: "500",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                fontFamily: "Inter",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#f8fff8";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "transparent";
              }}
            >
              <FaPlus size={isMobile ? 16 : 18} />
              Add Location
            </button>
          </div>
        ) : (
          <div
            style={{
              padding: isMobile ? "16px" : "20px",
              paddingBottom: isMobile ? "16px" : "20px",
              flexShrink: 0,
              backgroundColor: "white",
            }}
          >
            <button
              onClick={handleUseCurrentLocation}
              disabled={actionLoading === "current-location"}
              style={{
                width: "100%",
                padding: isMobile ? "16px" : "18px",
                backgroundColor: "#6B8E23",
                border: "1px solid #6B8E23",
                borderRadius: "12px",
                color: "#fff",
                fontSize: isMobile ? "15px" : "16px",
                fontWeight: "500",
                cursor:
                  actionLoading === "current-location"
                    ? "not-allowed"
                    : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                fontFamily: "Inter",
                transition: "all 0.2s ease",
                opacity: actionLoading === "current-location" ? 0.7 : 1,
              }}
              onMouseEnter={(e) => {
                if (!actionLoading) {
                  e.target.style.backgroundColor = "#5a7a1c";
                }
              }}
              onMouseLeave={(e) => {
                if (!actionLoading) {
                  e.target.style.backgroundColor = "#6B8E23";
                }
              }}
            >
              {actionLoading === "current-location" ? (
                <>
                  <div
                    className="spinner-border spinner-border-sm"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Getting location...
                </>
              ) : (
                <>
                  <FaCrosshairs size={isMobile ? 16 : 18} />
                  Use Current Location
                </>
              )}
            </button>
          </div>
        )}

        {/* Saved Addresses Section - Scrollable within fixed height */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            backgroundColor: "white",
            padding: isMobile ? "0 16px 16px" : "0 20px 20px",
            overflow: "hidden",
            position: "relative",
          }}
        >
          {/* Loading Overlay */}
          {loading && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "12px",
              }}
            >
              <div
                className="spinner-border"
                role="status"
                style={{
                  width: "3rem",
                  height: "3rem",
                  color: "#6B8E23",
                }}
              >
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}

          <h6
            style={{
              color: "#2c2c2c",
              fontFamily: "Inter",
              fontWeight: "600",
              marginBottom: "16px",
              fontSize: isMobile ? "16px" : "18px",
              paddingTop: "8px",
              lineHeight: "1.3",
              flexShrink: 0,
            }}
          >
            Saved Addresses
            {primaryAddressId && (
              <span
                style={{
                  fontSize: isMobile ? "12px" : "14px",
                  fontWeight: "400",
                  color: "#666",
                  display: "block",
                  marginTop: "4px",
                }}
              >
                Primary: {getPrimaryName(getPrimaryAddress())}
              </span>
            )}
          </h6>

          <div
            ref={savedAddressesRef}
            style={{
              flex: 1,
              overflowY: "auto",
              paddingRight: "4px",
              minHeight: "200px",
            }}
          >
            {savedAddresses.length === 0 && !loading ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px 20px",
                  color: "#666",
                  fontFamily: "Inter",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <FaMapMarkerAlt
                  size={isMobile ? 48 : 56}
                  color="#ccc"
                  style={{ marginBottom: "16px" }}
                />
                <p
                  style={{
                    margin: 0,
                    fontSize: isMobile ? "14px" : "16px",
                    lineHeight: "1.4",
                    color: "#888",
                  }}
                >
                  No saved addresses yet.
                </p>
                <p
                  style={{
                    margin: "8px 0 0 0",
                    fontSize: isMobile ? "13px" : "14px",
                    lineHeight: "1.4",
                    color: "#666",
                  }}
                >
                  Click "Add Location" above to add your first address.
                </p>
              </div>
            ) : (
              savedAddresses.map((address, index) => {
                const isSelected =
                  (selectedLocationId && selectedLocationId === address._id) ||
                  (!selectedLocationId && isPrimaryAddress(address._id));
                // console.log(
                //   selectedLocationId,
                //   address._id,
                //   isSelected,
                //   address
                // );
                return (
                  <div
                    key={address._id}
                    style={{
                      padding: isMobile ? "16px" : "18px",
                      border: isSelected
                        ? "2px solid #6B8E23"
                        : "1px solid #e0e0e0",
                      borderRadius: "12px",
                      marginBottom:
                        index < savedAddresses.length - 1 ? "12px" : "0",
                      backgroundColor: isSelected ? "#f8fff8" : "white",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: "12px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        <span
                          style={{
                            fontSize: isMobile ? "20px" : "22px",
                            marginRight: "12px",
                            marginTop: "2px",
                            flexShrink: 0,
                          }}
                        >
                          {getAddressIcon(address.addressType)}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              marginBottom: "6px",
                              flexWrap: "wrap",
                              gap: "6px",
                            }}
                          >
                            <h6
                              style={{
                                margin: 0,
                                color: "#2c2c2c",
                                fontFamily: "Inter",
                                fontWeight: "600",
                                fontSize: isMobile ? "14px" : "15px",
                                lineHeight: "1.3",
                              }}
                            >
                              {getPrimaryName(address)}
                            </h6>
                            {isPrimaryAddress(address._id) && (
                              <span
                                style={{
                                  fontSize: isMobile ? "10px" : "11px",
                                  color: "#6B8E23",
                                  fontWeight: "500",
                                  backgroundColor: "#f0f9f0",
                                  padding: "3px 8px",
                                  borderRadius: "4px",
                                  flexShrink: 0,
                                }}
                              >
                                PRIMARY
                              </span>
                            )}
                          </div>
                          <p
                            style={{
                              margin: 0,
                              color: "#666",
                              fontSize: isMobile ? "12px" : "13px",
                              fontFamily: "Inter",
                              lineHeight: "1.4",
                              wordBreak: "break-word",
                            }}
                          >
                            {address.fullAddress}
                          </p>
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                          alignItems: "flex-end",
                          flexShrink: 0,
                        }}
                      >
                        <button
                          onClick={() => handleDeliverHere(address)}
                          disabled={actionLoading === address._id}
                          style={{
                            backgroundColor: isSelected
                              ? "#6B8E23"
                              : "transparent",
                            border: isSelected ? "none" : "1px solid #6B8E23",
                            borderRadius: "8px",
                            color: isSelected ? "white" : "#6B8E23",
                            fontSize: isMobile ? "12px" : "13px",
                            fontWeight: "500",
                            padding: isMobile ? "8px 12px" : "10px 16px",
                            cursor:
                              actionLoading === address._id
                                ? "not-allowed"
                                : "pointer",
                            whiteSpace: "nowrap",
                            fontFamily: "Inter",
                            transition: "all 0.2s ease",
                            minWidth: "100px",
                            opacity: actionLoading === address._id ? 0.7 : 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "6px",
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected && !actionLoading) {
                              e.target.style.backgroundColor = "#6B8E23";
                              e.target.style.color = "white";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected && !actionLoading) {
                              e.target.style.backgroundColor = "transparent";
                              e.target.style.color = "#6B8E23";
                            }
                          }}
                        >
                          {actionLoading === address._id ? (
                            <div
                              className="spinner-border spinner-border-sm"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                          ) : isSelected ? (
                            <>
                              <FaCheckCircle
                                size={isMobile ? 12 : 14}
                                style={{ marginRight: "4px" }}
                              />
                              Selected
                            </>
                          ) : (
                            "Deliver here"
                          )}
                        </button>

                        <button
                          onClick={() => handleDeleteClick(address)}
                          disabled={actionLoading === `delete_${address._id}`}
                          style={{
                            backgroundColor: "transparent",
                            border: "1px solid #dc3545",
                            borderRadius: "6px",
                            color: "#dc3545",
                            fontSize: isMobile ? "11px" : "12px",
                            fontWeight: "500",
                            padding: isMobile ? "5px 25px" : "6px 12px",
                            cursor:
                              actionLoading === `delete_${address._id}`
                                ? "not-allowed"
                                : "pointer",
                            fontFamily: "Inter",
                            transition: "all 0.2s ease",
                            opacity:
                              actionLoading === `delete_${address._id}`
                                ? 0.7
                                : 1,
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                        >
                          {actionLoading === `delete_${address._id}` ? (
                            <div
                              className="spinner-border spinner-border-sm"
                              role="status"
                              style={{
                                width: isMobile ? "10px" : "12px",
                                height: isMobile ? "10px" : "12px",
                              }}
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                          ) : (
                            <>
                              <FaTrash size={isMobile ? 10 : 11} />
                              Delete
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Search Action Loading */}
      {actionLoading === "search" && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            zIndex: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            className="spinner-border"
            role="status"
            style={{
              width: "3.5rem",
              height: "3.5rem",
              color: "#6B8E23",
            }}
          >
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      <style>
        {`
          /* Custom scrollbar styling */
          div[style*="overflowY: auto"]::-webkit-scrollbar {
            width: 6px;
          }
          
          div[style*="overflowY: auto"]::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 10px;
          }
          
          div[style*="overflowY: auto"]::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 10px;
          }
          
          div[style*="overflowY: auto"]::-webkit-scrollbar-thumb:hover {
            background: #555;
          }
          
          /* Touch-friendly buttons */
          button {
            min-height: 44px;
            user-select: none;
          }
          
          /* Smooth animations */
          * {
            transition: background-color 0.2s ease, 
                        border-color 0.2s ease, 
                        opacity 0.2s ease,
                        transform 0.2s ease;
          }
          
          /* Prevent text selection */
          .no-select {
            user-select: none;
            -webkit-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
          }
          
          /* Modal animations */
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes slideUp {
            from {
              transform: translateY(100%);
            }
            to {
              transform: translateY(0);
            }
          }
          
          /* Apply animation based on device */
          @media (max-width: 768px) {
            div[style*="position: fixed"] {
              animation: slideUp 0.3s ease-out;
            }
          }
          
          @media (min-width: 769px) {
            div[style*="position: fixed"] {
              animation: fadeIn 0.3s ease-out;
            }
          }
        `}
      </style>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        show={showDeleteConfirm}
        onClose={handleCancelDelete}
        onConfirm={handleDeleteAddress}
        title="Delete Address"
        message={`Are you sure you want to delete this address?${
          addressToDelete ? `\n\n${getPrimaryName(addressToDelete)}` : ""
        }`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonStyle="danger"
      />
    </div>
  );
};

export default LocationModal2;
